import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
  CheckCircle2,
  CheckCircle,
  XCircle, 
  Calendar, 
  Users, 
  MapPin, 
  Clock,
  Phone,
  Mail,
  Building2,
  ChefHat,
  Wine,
  Music,
  Truck,
  DollarSign,
  FileText,
  Sparkles,
  PartyPopper,
  AlertCircle,
  CreditCard,
  Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  food: <ChefHat className="h-5 w-5" />,
  drinks: <Wine className="h-5 w-5" />,
  entertainment: <Music className="h-5 w-5" />,
  staffing: <Users className="h-5 w-5" />,
  logistics: <Truck className="h-5 w-5" />
};

const STATUS_INFO = {
  draft: { color: 'bg-gray-500', label: 'Draft' },
  sent: { color: 'bg-blue-500', label: 'Sent' },
  viewed: { color: 'bg-amber-500', label: 'Viewed' },
  pending: { color: 'bg-purple-500', label: 'Pending' },
  accepted: { color: 'bg-emerald-500', label: 'Accepted' },
  declined: { color: 'bg-red-500', label: 'Declined' },
  expired: { color: 'bg-gray-500', label: 'Expired' }
};

// Helper to format time to standard AM/PM format
const formatTimeToStandard = (time24) => {
  if (!time24) return 'TBD';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const standardHour = hour % 12 || 12;
  return `${standardHour}:${minutes} ${ampm}`;
};

export default function ProposalLandingPage() {
  const { proposalSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [acceptMessage, setAcceptMessage] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionComplete, setActionComplete] = useState(null); // 'accepted' or 'declined'
  const [bookingData, setBookingData] = useState(null); // Store booking info after acceptance

  // Analytics tracking
  const trackEvent = async (eventType, data = {}) => {
    if (!proposal?.proposal_id) return;
    try {
      await axios.post(`${API}/proposals/analytics/track`, {
        proposal_id: proposal.proposal_id,
        event_type: eventType,
        data
      });
    } catch (e) { /* silent fail */ }
  };

  // Track page view on load
  useEffect(() => {
    if (proposal?.proposal_id) {
      trackEvent('page_view');
      
      // Track time on page every 30 seconds
      const timeInterval = setInterval(() => {
        trackEvent('time_on_page', { seconds: 30 });
      }, 30000);

      // Track scroll depth
      let maxScroll = 0;
      const handleScroll = () => {
        const depth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
        if (depth > maxScroll + 10) {
          maxScroll = depth;
          trackEvent('scroll', { depth });
        }
      };
      window.addEventListener('scroll', handleScroll);

      // Track section visibility via IntersectionObserver
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackEvent('section_view', { section: entry.target.dataset.section });
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.3 });
      
      setTimeout(() => {
        document.querySelectorAll('[data-section]').forEach(el => observer.observe(el));
      }, 1000);

      return () => {
        clearInterval(timeInterval);
        window.removeEventListener('scroll', handleScroll);
        observer.disconnect();
      };
    }
  }, [proposal?.proposal_id]);

  useEffect(() => {
    fetchProposal();
  }, [proposalSlug]);

  // ----------------------------------------------------------------
  // After Stripe Checkout success, the customer lands here with
  // ?paid=true&session_id=cs_xxx. Hit the backend sync endpoint so the
  // proposal is marked paid + receipts are dispatched even if Stripe's
  // webhook didn't fire. Then refresh local proposal state to show
  // the PAID stamp + download button.
  // ----------------------------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPaidRedirect = params.get('paid') === 'true';
    const sessionId = params.get('session_id');
    if (!isPaidRedirect || !proposal?.proposal_id) return;
    let cancelled = false;
    (async () => {
      try {
        const url = `${API}/proposals/${proposal.proposal_id}/sync-payment` +
          (sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '');
        await axios.get(url);
      } catch (_) { /* webhook may have already handled it */ }
      if (cancelled) return;
      // Re-fetch so the UI shows payment_status=paid
      try {
        const r = await axios.get(`${API}/proposals/public/by-slug/${proposalSlug}`);
        if (!cancelled) {
          setProposal(r.data.proposal);
          if (r.data.proposal?.payment_status === 'paid') {
            toast.success('Payment received — thank you!');
          }
        }
      } catch (_) {}
      // Clean up the query string so a refresh doesn't re-sync.
      try {
        const clean = window.location.pathname;
        window.history.replaceState({}, '', clean);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [proposal?.proposal_id, proposalSlug]);

  const fetchProposal = async () => {
    try {
      // Try slug-based lookup first
      const response = await axios.get(`${API}/proposals/public/by-slug/${proposalSlug}`);
      setProposal(response.data.proposal);
      setCompanyInfo(response.data.company_info);
    } catch (err) {
      console.error('Failed to fetch proposal:', err);
      setError('Proposal not found or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/proposals/${proposal.proposal_id}/accept`, {
        client_name: proposal.client_name,
        message: acceptMessage
      });
      setShowAcceptDialog(false);
      
      // Store booking data for the confirmation screen
      setBookingData({
        booking_id: response.data.booking_id,
        workspace_id: response.data.workspace_id,
        checkout_url: response.data.checkout_url
      });
      
      setActionComplete('accepted');
      toast.success('Proposal accepted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/proposals/${proposal.proposal_id}/decline`, {
        reason: declineReason
      });
      setShowDeclineDialog(false);
      setActionComplete('declined');
      toast.success('Thank you for your feedback.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline proposal');
    } finally {
      setSubmitting(false);
    }
  };

  // Group items by category
  const groupedItems = proposal?.selected_items?.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading proposal...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Proposal Not Found</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </Card>
      </div>
    );
  }

  // Already actioned
  if (actionComplete || proposal.status === 'accepted' || proposal.status === 'declined') {
    const isAccepted = actionComplete === 'accepted' || proposal.status === 'accepted';
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full text-center p-8">
          {isAccepted ? (
            <>
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <PartyPopper className="h-10 w-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-600 mb-2">Proposal Accepted!</h2>
              <p className="text-muted-foreground mb-6">
                Thank you for choosing Eventism! To secure your event date and services, 
                please complete your payment below.
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Event:</strong> {proposal.event_name || proposal.title}
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Date:</strong> {proposal.event_date ? format(parseISO(proposal.event_date), 'MMMM d, yyyy') : 'TBD'}
                </p>
                <p className="text-3xl font-bold text-indigo-600 mt-3">
                  ${(proposal.total || 0).toFixed(2)}
                </p>
              </div>
              
              {/* Book Service Button - Always show payment option */}
              <Button 
                size="lg"
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-lg mb-4"
                onClick={() => {
                  if (bookingData?.checkout_url) {
                    window.location.href = bookingData.checkout_url;
                  } else {
                    // Navigate to checkout with booking info
                    navigate(`/checkout?proposal=${proposal.proposal_id}&amount=${proposal.total}`);
                  }
                }}
              >
                <CreditCard className="h-5 w-5 mr-2" />
                Pay Now - ${(proposal.total || 0).toFixed(2)}
              </Button>
              
              <p className="text-xs text-muted-foreground">
                Secure payment processing. Your event date will be confirmed upon payment.
              </p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-muted-foreground" />
              </div>
              <h2 className="text-2xl font-bold text-muted-foreground mb-2">Proposal Declined</h2>
              <p className="text-muted-foreground mb-6">
                We appreciate your consideration. If you change your mind or need assistance 
                with future events, we'd be happy to help.
              </p>
            </>
          )}
          <Button variant="outline" onClick={() => navigate('/')}>
            Visit Our Website
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{(proposal.title || 'Event Proposal') + ' | Eventism'}</title>
        <meta name="description" content={'Event proposal for ' + proposal.client_name} />
      </Helmet>

      <div className="min-h-screen bg-white pt-16 pb-12">
        {/* PAID confirmation banner shown when payment_status === 'paid'.
            Replaces the "Accept & Pay" CTA below and offers a one-click
            PDF download with the diagonal PAID stamp baked in. */}
        {proposal.payment_status === 'paid' && (
          <div className="max-w-4xl mx-auto px-4 mb-6" data-testid="proposal-paid-banner">
            <div className="rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 p-5 flex items-center gap-4 flex-wrap">
              <div className="flex-shrink-0">
                <div className="h-14 w-14 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center">
                  <CheckCircle className="h-7 w-7 text-emerald-500" />
                </div>
              </div>
              <div className="flex-1 min-w-[240px]">
                <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Payment received</p>
                <h3 className="text-2xl font-extrabold text-slate-900">PAID — ${(proposal.paid_amount || proposal.total || 0).toFixed(2)}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {proposal.paid_at ? `Paid on ${format(parseISO(proposal.paid_at), 'MMMM d, yyyy')}` : 'Thank you — your payment is confirmed.'}
                </p>
              </div>
              <Button
                size="lg"
                onClick={() => window.open(`${API}/proposals/public/${proposal.proposal_id}/pdf`, '_blank')}
                data-testid="download-paid-invoice"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <Download className="h-4 w-4 mr-2" /> Download paid invoice
              </Button>
            </div>
          </div>
        )}
        {/* Document Container - Max width like preview */}
        <div className="max-w-4xl mx-auto">
          {/* Proposal Document */}
          <div className="bg-white shadow-lg">
            {/* Header Image */}
            <div 
              className="h-48 bg-cover bg-center relative"
              style={{ backgroundImage: `url(${proposal.header_image || 'https://images.unsplash.com/photo-1753234506830-069cff1f25ae?w=1200&h=300&fit=crop'})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">EVENTISM</h1>
                  <p className="text-foreground/80">Premium Event Services</p>
                </div>
                {/* Proposal Number */}
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                  <p className="text-foreground/80 text-xs uppercase tracking-wider">Proposal</p>
                  <p className="text-foreground font-bold text-lg">{proposal.proposal_number || 'EP-NEW'}</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Title - Event Type on Date format */}
              <div className="text-center mb-8">
                <Badge className="bg-indigo-500 mb-3">EVENT PROPOSAL</Badge>
                <h2 className="text-4xl font-extrabold text-slate-900 mb-2">
                  {proposal.title || proposal.event_name || proposal.client_name}
                </h2>
                {/* Event Type on Date format */}
                {(proposal.event_type || proposal.event_date) && (
                  <p className="text-xl text-muted-foreground">
                    {proposal.event_type || 'Event'} on {proposal.event_date ? format(parseISO(proposal.event_date), 'MMMM d, yyyy') : 'Date TBD'}
                  </p>
                )}
              </div>

              {/* Client & Event Info Grid */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Client Details */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Client Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-slate-800">{proposal.client_name}</p>
                        {proposal.company_name && <p className="text-muted-foreground">{proposal.company_name}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-slate-800">{proposal.client_email}</span>
                    </div>
                    {proposal.client_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-slate-800">{proposal.client_phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Event Details - With Icons */}
                <div data-section="event-details" className="bg-slate-50 rounded-lg p-6">
                  <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Event Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground w-20">Type:</span>
                      <span className="font-medium text-slate-800">{proposal.event_type || 'Event'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground w-20">Date:</span>
                      <span className="font-medium text-slate-800">
                        {proposal.event_date ? format(parseISO(proposal.event_date), 'MMMM d, yyyy') : 'TBD'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground w-20">Time:</span>
                      <span className="font-medium text-slate-800">
                        {proposal.event_time ? formatTimeToStandard(proposal.event_time) : 'TBD'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground w-20">Guests:</span>
                      <span className="font-medium text-slate-800">{proposal.guest_count}</span>
                    </div>
                    {proposal.venue && (
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground w-20">Venue:</span>
                        <span className="font-medium text-slate-800">{proposal.venue}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery/Pickup Info */}
              {(proposal.delivery_date || proposal.pickup_date) && (
                <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="font-semibold text-amber-700 mb-4 flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    Equipment Delivery & Pickup
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    {proposal.delivery_date && (
                      <div>
                        <p className="text-amber-600 font-medium mb-1">Delivery</p>
                        <p className="text-muted-foreground">
                          {format(parseISO(proposal.delivery_date), 'MMMM d, yyyy')}
                          {proposal.delivery_time && ` at ${formatTimeToStandard(proposal.delivery_time)}`}
                        </p>
                      </div>
                    )}
                    {proposal.pickup_date && (
                      <div>
                        <p className="text-amber-600 font-medium mb-1">Pickup</p>
                        <p className="text-muted-foreground">
                          {format(parseISO(proposal.pickup_date), 'MMMM d, yyyy')}
                          {proposal.pickup_time && ` at ${formatTimeToStandard(proposal.pickup_time)}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Note From Eventism */}
              {proposal.basic_details && (
                <div className="mb-8">
                  <h3 className="font-semibold text-indigo-600 mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Note From Eventism
                  </h3>
                  <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-r-lg">
                    <p className="text-muted-foreground whitespace-pre-line">{proposal.basic_details}</p>
                  </div>
                </div>
              )}
              {/* Service Overview */}
              <div data-section="service-overview" className="mb-8">
                <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Service Overview
                </h3>
                
                <div className="space-y-6">
                  {proposal.selected_items?.map((item, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="flex">
                        {/* Service Image */}
                        {item.image_url && (
                          <div className="w-40 h-40 flex-shrink-0">
                            <img 
                              src={item.image_url} 
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        {/* Service Details */}
                        <div className="flex-1 p-5">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="text-lg font-bold text-slate-900">{item.name}</h4>
                              <span className="text-xs uppercase tracking-wide text-indigo-500 font-medium">{item.category}</span>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-muted-foreground">
                                ${item.price}/{item.unit || 'person'}
                              </p>
                            </div>
                          </div>
                          
                          {/* What to Expect - Always show */}
                          <div className="mb-3">
                            <p className="text-sm font-medium text-muted-foreground mb-1">What to Expect:</p>
                            <p className="text-sm text-muted-foreground">
                              {item.what_to_expect || 'Our professional team will deliver exceptional service for your event, handling all setup, service, and cleanup.'}
                            </p>
                          </div>
                          
                          {/* What's Included - Always show */}
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">What's Included:</p>
                            <div className="flex flex-wrap gap-2">
                              {(item.whats_included && item.whats_included.length > 0) ? (
                                item.whats_included.map((inc, i) => (
                                  <span 
                                    key={i} 
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full"
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                    {inc}
                                  </span>
                                ))
                              ) : (
                                <>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Professional Setup
                                  </span>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Service Staff
                                  </span>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Cleanup
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Financial Detail */}
              <div data-section="financial-detail" className="mb-8">
                <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Service Financial Detail
                </h3>
                
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Service</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Rate</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Qty</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposal.selected_items?.map((item, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {item.image_url && (
                                <img 
                                  src={item.image_url} 
                                  alt={item.name} 
                                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                />
                              )}
                              <div>
                                <p className="font-medium text-slate-900">{item.name}</p>
                                <p className="text-xs text-muted-foreground">{item.category}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-muted-foreground">
                            ${item.price}/{item.unit || 'person'}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-muted-foreground">
                            {item.quantity}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-slate-900">
                            ${(item.price * item.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pricing Summary */}
              <div data-section="pricing" className="bg-slate-50 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Investment Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium text-slate-800">${(proposal.subtotal || 0).toFixed(2)}</span>
                  </div>
                  {proposal.discount > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Discount ({proposal.discount_percent || 0}%)</span>
                      <span>-${(proposal.discount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Service Fee ({proposal.admin_fee_percent || 18}%)</span>
                    <span className="text-slate-800">${(proposal.admin_fee || 0).toFixed(2)}</span>
                  </div>
                  {proposal.delivery_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Delivery Fee</span>
                      <span className="text-slate-800">${(proposal.delivery_fee || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax ({proposal.tax_percent || 8}%)</span>
                    <span className="text-slate-800">${(proposal.tax || 0).toFixed(2)}</span>
                  </div>
                  <Separator className="my-3" />
                  <div className="flex justify-between text-2xl font-bold">
                    <span className="text-slate-900">Total</span>
                    <span className="text-indigo-600">${(proposal.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              {proposal.terms_conditions && (
                <div className="mb-8">
                  <h3 className="font-semibold text-muted-foreground mb-3">Terms & Conditions</h3>
                  <div className="bg-slate-50 rounded-xl p-4 text-sm text-muted-foreground whitespace-pre-line">
                    {proposal.terms_conditions}
                  </div>
                </div>
              )}

              {/* Valid Until */}
              {proposal.valid_until && (
                <div className="text-center text-amber-600 bg-amber-50 rounded-lg p-3 mb-8">
                  <Clock className="h-4 w-4 inline mr-1" />
                  This proposal is valid until {format(new Date(proposal.valid_until), 'MMMM d, yyyy')}
                </div>
              )}

              {/* Action Buttons — hidden once payment is received */}
              {proposal.payment_status === 'paid' ? (
                <div className="text-center bg-emerald-500/10 rounded-xl border border-emerald-500/30 p-6">
                  <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                  <p className="font-semibold text-slate-900">This invoice has been paid in full.</p>
                  <p className="text-sm text-muted-foreground mt-1">No further action needed. Use the Download button at the top of this page for your records.</p>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg px-8"
                    onClick={() => { trackEvent('cta_click', { action: 'accept' }); setShowAcceptDialog(true); }}
                  >
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                    Accept Proposal
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="text-lg px-8 border-slate-300 text-muted-foreground hover:bg-slate-100 hover:text-slate-900"
                    onClick={() => setShowDeclineDialog(true)}
                  >
                    <XCircle className="h-5 w-5 mr-2" />
                    Decline
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p className="mb-2">
              <Phone className="h-4 w-4 inline mr-1" />
              {companyInfo?.phone}
              <span className="mx-3">|</span>
              <Mail className="h-4 w-4 inline mr-1" />
              {companyInfo?.email}
            </p>
            <p>{companyInfo?.address}</p>
            <p className="mt-4 text-indigo-500 font-medium">
              Thank you for considering Eventism for your event!
            </p>
          </div>
        </div>

        {/* Accept Dialog */}
        <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="h-6 w-6" />
                Accept Proposal
              </DialogTitle>
              <DialogDescription>
                You're about to accept this proposal for ${proposal?.total?.toFixed(2)}.
                Our team will contact you shortly to finalize the details.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="block text-sm font-medium mb-2">
                Message for our team (optional)
              </label>
              <Textarea
                value={acceptMessage}
                onChange={(e) => setAcceptMessage(e.target.value)}
                placeholder="Any special requests or notes..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAcceptDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAccept} 
                disabled={submitting}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {submitting ? 'Processing...' : 'Confirm Acceptance'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Decline Dialog */}
        <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Decline Proposal</DialogTitle>
              <DialogDescription>
                We're sorry to see you go. Would you mind sharing why this proposal 
                didn't work for you? Your feedback helps us improve.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="block text-sm font-medium mb-2">
                Reason (optional)
              </label>
              <Textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Budget constraints, timing, found another vendor..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeclineDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleDecline} 
                disabled={submitting}
                variant="destructive"
              >
                {submitting ? 'Processing...' : 'Decline Proposal'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
